(() => {
  const configElement = document.getElementById('wt-seven-day-purchase-config');
  if (!configElement) return;

  let config;
  try {
    config = JSON.parse(configElement.textContent);
  } catch {
    return;
  }

  const variants = new Set(config.variantIds.map(String));
  const messages = config.messages;
  const pendingForms = new WeakSet();
  const permittedSubmits = new WeakSet();
  let knownHasPass = Boolean(config.initialHasPass);
  let pending = Promise.resolve();
  let checkingOut = false;
  let scheduled = false;

  function isPass(value) {
    return variants.has(String(value));
  }

  function passVariant(form) {
    if (!(form instanceof HTMLFormElement)) return null;
    const action = new URL(form.action, window.location.href);
    if (!action.pathname.replace(/\/$/, '').endsWith('/cart/add')) return null;
    const variant = new FormData(form).get('id');
    return isPass(variant) ? String(variant) : null;
  }

  function hasPassInCart() {
    const roots = document.querySelectorAll('cart-items, cart-drawer-items');
    if (!roots.length) return knownHasPass;
    return Array.from(roots).some((root) =>
      Array.from(root.querySelectorAll('[data-quantity-variant-id]')).some((input) =>
        isPass(input.dataset.quantityVariantId)
      )
    );
  }

  function showMessage(scope, text, error = false) {
    const container = scope || document.querySelector('main') || document.body;
    let message = container.querySelector('[data-wt-seven-day-message]');
    if (!message) {
      message = document.createElement('p');
      message.dataset.wtSevenDayMessage = '';
      message.className = 'wt-seven-day-message';
      message.setAttribute('role', 'status');
      message.setAttribute('aria-live', 'polite');
      container.append(message);
    }
    message.setAttribute('role', error ? 'alert' : 'status');
    message.textContent = text;
  }

  function busy(button, value) {
    if (!button) return;
    if (value) {
      button.dataset.wtWasDisabled = button.disabled ? 'true' : 'false';
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
    } else {
      if (button.dataset.wtWasDisabled !== undefined) {
        button.disabled = button.dataset.wtWasDisabled === 'true';
        delete button.dataset.wtWasDisabled;
      }
      button.removeAttribute('aria-busy');
    }
  }

  function exclusive(callback) {
    const run = () => window.navigator.locks?.request
      ? window.navigator.locks.request('wisertrail-seven-day-cart', callback)
      : callback();
    const result = pending.then(run, run);
    pending = result.catch(() => {});
    return result;
  }

  async function request(url, options = {}) {
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 15000);
    try {
      const response = await fetch(url, {
        ...options,
        credentials: 'same-origin',
        cache: 'no-store',
        signal: controller.signal,
      });
      if (!response.ok) throw new Error('cart_request_failed');
      const result = await response.json();
      if (result.status >= 400 || result.errors) throw new Error('cart_request_failed');
      return result;
    } finally {
      window.clearTimeout(timeout);
    }
  }

  function checkedCart(cart) {
    if (!cart || !Array.isArray(cart.items)) throw new Error('cart_unavailable');
    knownHasPass = cart.items.some((item) => isPass(item.variant_id));
    return cart;
  }

  async function readCart() {
    return checkedCart(await request(`${config.cartUrl.replace(/\/$/, '')}.js`));
  }

  function nextAdjustment(cart) {
    const seen = new Set();
    for (const item of cart.items) {
      if (!isPass(item.variant_id)) continue;
      if (!item.key || !Number.isInteger(item.quantity) || item.quantity < 1) {
        throw new Error('pass_line_unavailable');
      }
      if (item.selling_plan_allocation) throw new Error('pass_requires_one_time_purchase');
      const id = String(item.variant_id);
      if (seen.has(id)) return { id: item.key, quantity: 0 };
      seen.add(id);
      if (item.quantity !== 1) return { id: item.key, quantity: 1 };
    }
    return null;
  }

  async function normalizePasses(cart) {
    let changed = false;
    for (let attempt = 0; attempt < 50; attempt += 1) {
      const adjustment = nextAdjustment(cart);
      if (!adjustment) return { cart, changed };
      cart = checkedCart(await request(`${config.changeUrl.replace(/\.js$/, '')}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(adjustment),
      }));
      changed = true;
    }
    throw new Error('cart_changed_too_many_times');
  }

  async function addPass(form, variant, submitter) {
    if (pendingForms.has(form)) return;
    if (!config.loggedIn) {
      window.location.assign(config.loginUrl);
      return;
    }
    pendingForms.add(form);
    busy(submitter, true);
    const scope = form.closest('product-form') || form;
    showMessage(scope, messages.checking);
    try {
      await exclusive(async () => {
        const before = await normalizePasses(await readCart());
        if (before.cart.items.some((item) => String(item.variant_id) === variant)) {
          showMessage(scope, messages.alreadyAdded);
          window.location.assign(config.cartUrl);
          return;
        }

        const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        const data = new FormData(form);
        data.set('id', variant);
        data.set('quantity', '1');
        data.delete('selling_plan');
        if (cart?.getSectionsToRender) {
          data.set('sections', cart.getSectionsToRender().map((section) => section.id).join(','));
          data.set('sections_url', window.location.pathname);
          cart.setActiveElement?.(document.activeElement);
        }
        const added = await request(`${config.addUrl.replace(/\.js$/, '')}.js`, {
          method: 'POST',
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: data,
        });
        const after = await normalizePasses(await readCart());
        if (!after.cart.items.some((item) => String(item.variant_id) === variant)) {
          throw new Error('pass_not_added');
        }
        showMessage(scope, messages.added);
        if (after.changed || !cart?.renderContents || !added.sections) {
          window.location.assign(config.cartUrl);
          return;
        }
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'wt-seven-day-purchase',
            productVariantId: variant,
            cartData: added,
          });
        }
        cart.classList.remove('is-empty');
        cart.renderContents(added);
        applyUi();
      });
    } catch {
      showMessage(scope, messages.error, true);
    } finally {
      pendingForms.delete(form);
      busy(submitter, false);
    }
  }

  function checkoutScope(button, form) {
    return button?.closest('cart-drawer, .cart__footer') || form || document.querySelector('main');
  }

  function matchesCartForm(form, cart) {
    if (!form) return true;
    const quantities = Array.from(form.elements).filter((input) => input.name === 'updates[]');
    return quantities.length === cart.items.length && quantities.every((input, index) =>
      input.dataset.quantityLineKey === cart.items[index].key &&
      Number(input.value) === cart.items[index].quantity
    );
  }

  async function checkout(button, form, href) {
    if (checkingOut) return;
    if (!config.loggedIn) {
      window.location.assign(config.loginUrl);
      return;
    }
    checkingOut = true;
    busy(button, true);
    const scope = checkoutScope(button, form);
    showMessage(scope, messages.checking);
    try {
      await exclusive(async () => {
        const result = await normalizePasses(await readCart());
        if (result.changed || !matchesCartForm(form, result.cart)) {
          showMessage(scope, messages.cartUpdated);
          const target = new URL(config.cartUrl, window.location.origin);
          target.searchParams.set('wt_pass_updated', '1');
          window.location.assign(target.href);
          return;
        }
        busy(button, false);
        if (form && button && button.form === form && button.name === 'checkout') {
          permittedSubmits.add(form);
          try {
            form.requestSubmit(button);
          } finally {
            permittedSubmits.delete(form);
          }
        } else {
          window.location.assign(href || config.checkoutUrl);
        }
      });
    } catch {
      showMessage(scope, messages.error, true);
    } finally {
      busy(button, false);
      checkingOut = false;
    }
  }

  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (permittedSubmits.has(form)) {
      permittedSubmits.delete(form);
      return;
    }
    const variant = passVariant(form);
    if (variant) {
      event.preventDefault();
      event.stopImmediatePropagation();
      addPass(form, variant, event.submitter || form.querySelector('[type="submit"]'));
      return;
    }
    if (event.submitter?.name === 'checkout' && hasPassInCart()) {
      event.preventDefault();
      event.stopImmediatePropagation();
      checkout(event.submitter, form);
    }
  }, true);

  document.addEventListener('click', (event) => {
    const button = event.target.closest('button[name="checkout"], input[name="checkout"], a[href]');
    if (!button || !hasPassInCart()) return;
    let href;
    if (button instanceof HTMLAnchorElement) {
      const url = new URL(button.href, window.location.href);
      if (url.origin !== window.location.origin || !/\/checkout\/?$/.test(url.pathname)) return;
      href = url.href;
    } else if (button.disabled) {
      return;
    }
    event.preventDefault();
    event.stopImmediatePropagation();
    checkout(button, button.form || null, href);
  }, true);

  function applyUi() {
    scheduled = false;
    for (const input of document.querySelectorAll('[data-quantity-variant-id]')) {
      if (!isPass(input.dataset.quantityVariantId)) continue;
      input.readOnly = true;
      input.max = '1';
      const row = input.closest('.cart-item') || input.closest('tr');
      if (!row) continue;
      row.dataset.wtSevenDayLine = '';
      if (!row.querySelector('[data-wt-seven-day-row-note]')) {
        const note = document.createElement('small');
        note.dataset.wtSevenDayRowNote = '';
        note.className = 'wt-seven-day-cart-note';
        note.textContent = messages.onePerCheckout;
        (input.closest('.cart-item__quantity') || input.parentElement.parentElement).append(note);
      }
    }
    for (const form of document.querySelectorAll('form[action*="/cart/add"]')) {
      if (!passVariant(form)) continue;
      for (const input of Array.from(form.elements)) {
        if (input.name === 'quantity') {
          input.value = '1';
          input.max = '1';
          input.readOnly = true;
        }
      }
    }
    const hasPass = hasPassInCart();
    for (const express of document.querySelectorAll('.cart__dynamic-checkout-buttons')) {
      if (hasPass) {
        if (!express.hasAttribute('data-wt-seven-day-express-hidden')) {
          express.dataset.wtSevenDayExpressHidden = express.hidden ? 'already' : 'added';
          express.hidden = true;
        }
      } else if (express.hasAttribute('data-wt-seven-day-express-hidden')) {
        if (express.dataset.wtSevenDayExpressHidden === 'added') express.hidden = false;
        delete express.dataset.wtSevenDayExpressHidden;
      }
    }
  }

  const observer = new MutationObserver(() => {
    if (scheduled) return;
    scheduled = true;
    window.requestAnimationFrame(applyUi);
  });
  observer.observe(document.body, { childList: true, subtree: true });
  applyUi();
  if (new URL(window.location.href).searchParams.has('wt_pass_updated')) {
    showMessage(document.querySelector('.cart__footer') || document.querySelector('main'), messages.cartUpdated);
  }
})();

